import { usePageCrumb } from '@ps/ui-kit/components/app-shell';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { useBlurValidation } from '@ps/ui-kit/lib/use-blur-validation';
import { minChars } from '@ps/ui-kit/lib/validate';
import { identityRedoItem } from '@ps/api-client/applicant';
import { ApiError } from '@ps/api-client/http';
import { staffApi, type ApplicationDetail, type StaffApp } from '@ps/api-client/staff';
import { Alert } from '@ps/ui-kit/components/alert';
import { Button } from '@ps/ui-kit/components/button';
import { Card, CardTitle } from '@ps/ui-kit/components/card';
import { Dialog } from '@ps/ui-kit/components/dialog';
import { Checkbox, Field, Textarea } from '@ps/ui-kit/components/field';
import { Input } from '@ps/ui-kit/components/input';
import { Pill } from '@ps/ui-kit/components/pill';
import { ErrorState, Loading } from '@ps/ui-kit/components/states';
import { Tabs } from '@ps/ui-kit/components/tabs';
import { addDaysIso, ageDays, date, titleCase } from '@ps/ui-kit/lib/format';
import { ActionRail } from '../action-rail';
import { OwnWordingConfirm, RequestItemsPicker, useRequestItems } from '../request-items';
import { ACTION_LABEL, BAND_TONE, STATE_NAME, STATE_TONE } from './labels';
import { PartyGraph } from './party-graph';
import { AuditTab, ChecklistTab, DocumentsTab, ProfileTab, RiskTab, ScreeningTab } from './tabs';

// A-02 Application detail frame: header · party graph · tabs · action rail. The rail is exactly the server's
// availableActions; navigation-only actions (dispose, checklist, documents) jump to their tab.

const TAB_FOR: Record<string, string> = { DISPOSE_HIT: 'screening', MLRO_DECIDE_HIT: 'screening', UPDATE_CHECKLIST: 'cdd', DECIDE_DOCUMENT: 'documents' };
const errMsg = (e: unknown) => (e instanceof ApiError ? e.body.message : 'Something went wrong. Please try again.');

export function ApplicationDetailView({ app, id, roleLabel }: { app: StaffApp; id: string; roleLabel: string }) {
  const api = React.useMemo(() => staffApi(app), [app]);
  const qc = useQueryClient();
  const key = ['application', app, id];
  const q = useQuery({ queryKey: key, queryFn: () => api.get(id) });
  usePageCrumb(q.data?.application.reference);
  const [tab, setTab] = React.useState('profile');
  const [dialog, setDialog] = React.useState<'REQUEST_INFO' | 'RECOMMEND' | 'APPROVE' | 'REJECT' | null>(null);
  const [busy, setBusy] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const openDialog = (d: typeof dialog) => { setError(null); setDialog(d); };
  // Some changes (accepting a document, an identity check result) do not change the application's version, so the
  // tabs' own lists are refreshed explicitly after every change, not only when the version moves.
  const updated = (d: ApplicationDetail) => {
    qc.setQueryData(key, d);
    void qc.invalidateQueries({ queryKey: ['applications', app] });
    for (const k of ['docs', 'idv', 'audit']) void qc.invalidateQueries({ queryKey: [k, id] });
    void qc.invalidateQueries({ queryKey: ['work'] });   // "Waiting for you" and the menu badges
  };

  if (q.isPending) return <Loading label="Loading application…" />;
  if (q.error) return <ErrorState message={errMsg(q.error)} onRetry={() => q.refetch()} />;
  const detail = q.data!;
  const a = detail.application;

  async function run(action: string, fn: () => Promise<ApplicationDetail>, success?: (d: ApplicationDetail) => string) {
    setBusy(action); setError(null); setNotice(null);
    try { const d = await fn(); updated(d); setDialog(null); if (success) setNotice(success(d)); }
    catch (e) {
      setError(errMsg(e));
      // The state moved on: close any dialog and show the server's current actions with the message.
      if (e instanceof ApiError && e.status === 409) { setDialog(null); void q.refetch(); }
    } finally { setBusy(null); }
  }

  const cdd = detail.checklistProgress;
  const railLabels = { ...ACTION_LABEL, UPDATE_CHECKLIST: cdd?.complete ? 'CDD checklist complete' : 'CDD checklist' };

  function onAction(action: string) {
    if (TAB_FOR[action]) { setTab(TAB_FOR[action]!); return; }
    if (action === 'RETRY_SCREENING') { void run(action, () => api.retryScreening(a.id)); return; }
    if (action === 'REQUEST_INFO' || action === 'RECOMMEND' || action === 'APPROVE' || action === 'REJECT') openDialog(action);
  }

  const openRfi = a.rfis.find(r => r.status === 'OPEN');
  const age = ageDays(a.submittedAt);
  const tabProps = { api, detail, onUpdated: updated };
  return (
    <div>
      {/* Prototype A-02 header: the name, then state, risk, channel and type as sentence-case pills (the reference is in the breadcrumb). */}
      <header className="mb-4 flex flex-wrap items-center gap-2">
        <h1 className="mr-2 font-display text-xl font-bold text-ink">{a.legalName}</h1>
        <Pill tone={STATE_TONE[a.state] ?? 'neu'}>{STATE_NAME[a.state] ?? titleCase(a.state)}</Pill>
        {a.risk && <Pill tone={BAND_TONE[a.risk.band] ?? 'neu'}>{titleCase(a.risk.band)} risk</Pill>}
        <Pill tone="neu">Non-face-to-face</Pill>
        <Pill tone="neu">{titleCase(a.type)}</Pill>
        {a.pep && <Pill tone="bad">PEP</Pill>}
      </header>
      {a.state === 'SCREENING_HIT' && (
        <Alert tone="bad" className="mb-4" title="Application is blocked pending screening adjudication">No transition out of SCREENING_HIT is available until every possible match is dispositioned with a recorded rationale. Guideline ¶6.18.</Alert>
      )}
      {a.screeningFailedAt && a.state === 'SUBMITTED' && <Alert tone="warn" className="mb-4">Screening could not complete (provider unavailable). The application stays here until screening succeeds — it does not move on without it.</Alert>}
      {error && !dialog && <Alert tone="bad" className="mb-4">{error}</Alert>}
      {notice && <Alert tone="ok" className="mb-4">{notice}</Alert>}

      {/* Prototype A-02: party graph, tabs and the action rail in three columns from 1400 px (the Screening tab lays out
          by its own width); below that the rail stays beside the content and the parties go above it. */}
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_16rem] desk:grid-cols-[15rem_minmax(0,1fr)_16rem]">
        <Card className="h-fit xl:col-start-1 xl:row-start-1 desk:row-span-1"><CardTitle eyebrow>Party graph</CardTitle><PartyGraph app={a} uboIds={detail.beneficialOwnerIds} /></Card>
        <Card className="min-w-0 overflow-x-auto xl:col-start-1 xl:row-start-2 desk:col-start-2 desk:row-start-1">
          <Tabs value={tab} onValueChange={setTab} tabs={[
            { value: 'profile', label: 'Profile', content: <ProfileTab {...tabProps} /> },
            { value: 'documents', label: 'Documents', content: <DocumentsTab {...tabProps} /> },
            { value: 'screening', label: 'Screening', content: <ScreeningTab {...tabProps} /> },
            { value: 'risk', label: 'Risk', content: <RiskTab {...tabProps} /> },
            { value: 'cdd', label: 'CDD checklist', content: <ChecklistTab {...tabProps} /> },
            { value: 'audit', label: 'Audit trail', content: <AuditTab {...tabProps} /> },
          ]} />
        </Card>
        <div className="space-y-4 xl:col-start-2 xl:row-span-2 xl:row-start-1 desk:col-start-3 desk:row-span-1">
          <ActionRail state={a.state} role={roleLabel} actions={detail.availableActions} labels={railLabels} primary={detail.availableActions.find(x => x !== 'REJECT')} done={cdd?.complete ? ['UPDATE_CHECKLIST'] : []} progress={cdd ? { UPDATE_CHECKLIST: cdd } : undefined} onAction={onAction} busy={busy} explanation={detail.approveExplanation}>
            {detail.documentsAttention && (
              <button onClick={() => setTab('documents')} className="mt-3 block w-full rounded-lg border border-warn/40 bg-warn-bg p-2 text-left text-xs text-ink hover:underline">
                {detail.documentsAttention}
              </button>
            )}
            <div className="mt-3 border-t border-line pt-3 text-[13px] text-ink-2">
              <p className="eyebrow">SLA</p>
              <p>Age <span className="font-semibold text-ink">{age === null ? 'not submitted' : `${age} day${age === 1 ? '' : 's'}`}</span></p>
              {openRfi && <p>RFI response due <span className="font-semibold text-ink">{date(openRfi.dueDate)}</span></p>}
              {a.nextReviewDue && <p>Next review <span className="font-semibold text-ink">{date(a.nextReviewDue)}</span></p>}
            </div>
          </ActionRail>
        </div>
      </div>

      <RfiComposer people={a.parties.filter(p => p.kind === 'INDIVIDUAL').map(p => ({ id: p.id, name: p.name }))} open={dialog === 'REQUEST_INFO'} onClose={() => setDialog(null)} busy={busy === 'REQUEST_INFO'} error={error}
        onSend={b => run('REQUEST_INFO', () => api.rfi(a.id, { ...b, version: a.version }), () => 'Request sent. The applicant sees it on their application page.')} />
      <ConfirmDialog open={dialog === 'RECOMMEND'} onClose={() => setDialog(null)} title="Recommend this application"
        body={<>Risk is re-assessed now. A HIGH band or a PEP routes the application to the Compliance Officer automatically; otherwise it goes to the Operations Manager. You will not be able to approve it yourself.</>}
        confirm="Recommend" busy={busy === 'RECOMMEND'} error={error} onConfirm={() => run('RECOMMEND', () => api.recommend(a.id, a.version), d => `Recommended · ${d.application.risk?.band ?? ''} · routed to ${d.application.state === 'CO_REVIEW' ? 'the Compliance Officer' : 'the Operations Manager'}.`)} />
      <ConfirmDialog open={dialog === 'APPROVE'} onClose={() => setDialog(null)} title="Approve and open the account"
        body={<>This creates the customer record and gives the applicant access as company admin. Recommended by another person (four eyes).</>}
        confirm="Approve" busy={busy === 'APPROVE'} error={error} onConfirm={() => run('APPROVE', async () => { const r = await api.approve(a.id, a.version); return r; }, () => 'Approved. The customer account has been created.')} />
      <RejectDialog open={dialog === 'REJECT'} onClose={() => setDialog(null)} busy={busy === 'REJECT'} error={error} onReject={reason => run('REJECT', () => api.reject(a.id, reason, a.version), () => 'Application rejected.')} />
    </div>
  );
}

function ConfirmDialog({ open, onClose, title, body, confirm, busy, onConfirm, error }: { open: boolean; onClose: () => void; title: string; body: React.ReactNode; confirm: string; busy: boolean; onConfirm: () => void; error: string | null }) {
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()} title={title} description={body}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={onConfirm} disabled={busy}>{busy ? 'Working…' : confirm}</Button></>}>
      {error ? <Alert tone="bad">{error}</Alert> : <span />}
    </Dialog>
  );
}

function RejectDialog({ open, onClose, busy, onReject, error }: { open: boolean; onClose: () => void; busy: boolean; onReject: (reason: string) => void; error: string | null }) {
  const [reason, setReason] = React.useState('');
  const v = useBlurValidation({ reason: () => minChars(reason, 20) });
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()} title="Reject this application" description="Internal reason, recorded in the audit trail. The applicant is not told the reason."
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="destructive" disabled={busy || reason.trim().length < 20} onClick={() => onReject(reason)}>{busy ? 'Working…' : 'Reject'}</Button></>}>
      <Field id="reject-reason" label="Reason" hint={`${reason.trim().length} / 20 characters minimum`} error={v.errorOf('reason')}><Textarea id="reject-reason" value={reason} onChange={e => setReason(e.target.value)} {...v.field('reason', 'reject-reason')} /></Field>
      {error && <Alert tone="bad" className="mt-3">{error}</Alert>}
    </Dialog>
  );
}

const RFI_TEMPLATE = 'Thank you for your application. To continue, please provide the items listed below. You can upload them on your application page.';
/** The policy's response window for requests (requests.responseDays); the server applies it when no date is chosen. */
const RESPONSE_DAYS = 14;

/**
 * A-09: staff tick items from the standard list (ADR-096); their own wording only behind a second confirmation. The
 * message is pre-filled with neutral wording; the server refuses any mention of screening, risk or suspicion and logs
 * the attempt as blocked.
 */
export function RfiComposer({ open, onClose, onSend, busy, error, people = [] }: { open: boolean; onClose: () => void;
  onSend: (b: { items: string[]; message: string; dueDate?: string; freeTextConfirmed?: boolean }) => void; busy: boolean; error?: string | null;
  people?: Array<{ id: string; name: string }> }) {
  const r = useRequestItems();
  const [message, setMessage] = React.useState(RFI_TEMPLATE);
  const [due, setDue] = React.useState(() => addDaysIso(new Date(), RESPONSE_DAYS));
  const v = useBlurValidation({
    message: () => (!message.trim() ? 'Write a message to the applicant' : null),
    due: () => (!due ? 'Choose a due date' : due < new Date().toISOString().slice(0, 10) ? 'The due date is in the past' : null),
  });
  const send = () => { if (v.touchAll() && r.readyToSend()) onSend({ items: r.all, message, dueDate: due, ...r.confirmation() }); };
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()} title="Request information" wide
      description="The applicant sees this on their application page. Keep it neutral: never mention screening, risk, matches or suspicion."
      footer={r.confirming
        ? <><Button variant="ghost" onClick={() => r.setConfirming(false)}>Back</Button><Button disabled={busy} onClick={send}>{busy ? 'Sending…' : 'Send with my wording'}</Button></>
        : <><Button variant="ghost" onClick={onClose}>Cancel</Button><Button disabled={busy || !r.all.length} onClick={send}>{busy ? 'Sending…' : 'Send request'}</Button></>}>
      {r.confirming ? <OwnWordingConfirm text={r.own} /> : (<>
        <RequestItemsPicker s={r} where="APPLICATION" idPrefix="rfi">
          {people.length > 0 && (
            <div className="mt-3">
              <p className="text-[13px] font-semibold text-ink-2">Online identity check (photo ID and selfie with our verification partner)</p>
              <p className="text-xs text-ink-3">Not part of the application: ask for it when the uploaded identity document is not enough, or to repeat a check.</p>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {people.map(p => {
                  const item = identityRedoItem(p.name);
                  return <Checkbox key={p.id} label={p.name} checked={r.items.includes(item)} onChange={e => r.toggle(item, e.target.checked)} />;
                })}
              </div>
            </div>
          )}
        </RequestItemsPicker>
        <Field id="rfi-message" label="Message to the applicant" className="mt-4" error={v.errorOf('message')}><Textarea id="rfi-message" value={message} onChange={e => setMessage(e.target.value)} {...v.field('message', 'rfi-message')} /></Field>
        {!r.all.length && <p className="mt-2 text-xs text-ink-3">Choose at least one item from the list.</p>}
        <Field id="rfi-due" label="Due date" hint={`Default: ${RESPONSE_DAYS} days`} className="mt-4 max-w-48" error={v.errorOf('due')}><Input id="rfi-due" type="date" value={due} onChange={e => setDue(e.target.value)} {...v.field('due', 'rfi-due')} /></Field>
      </>)}
      {error && <Alert tone="bad" className="mt-3">{error}</Alert>}
    </Dialog>
  );
}
